// A cropped image/video layer's box (x/y/w/h) is exactly the size of what's left after cropping — like it was
// snipped down — while `props.crop` records which fraction (0-1) of the layer's original, uncropped extent that
// still is. Cropping an edge trims that fraction and shrinks the box by the same amount, so the remaining content
// keeps its own scale (nothing stretches); an ordinary resize afterwards changes the box only, which — since the
// content is a percentage of the box (below) — scales the whole cropped result together, crop fraction unchanged.

export const hasCrop = layer => !!layer?.props?.crop;

const round = n => Math.round(n * 10000) / 10000 || 0;
const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

// CSS left/top/width/height (percentages, relative to the box) that render only `crop`'s share of the layer's full
// extent, at that extent's own scale. No crop (null) renders exactly as an uncropped layer always has.
export function cropContentStyle(crop) {
	if(!crop) return { left: '0', top: '0', width: '100%', height: '100%' };
	return {
		left: `${round(-crop.x / crop.w * 100)}%`,
		top: `${round(-crop.y / crop.h * 100)}%`,
		width: `${round(100 / crop.w)}%`,
		height: `${round(100 / crop.h)}%`,
	};
}

// The full, uncropped size a layer's content would be, given its current box and crop.
export function naturalSize(layer) {
	const c = layer.props?.crop;
	return c ? { w: layer.w / c.w, h: layer.h / c.h } : { w: layer.w, h: layer.h };
}

// The smallest a cropped box (or an uncropped remainder) can shrink to, in canvas px.
const MIN_BOX = 8;

// What a crop-handle drag needs from the layer as it is right now, before any of it changes — the drag computes
// against this fixed snapshot throughout, not tick-to-tick, so it can't drift.
export function startCropResize(layer) {
	const { w: naturalW, h: naturalH } = naturalSize(layer);
	const c = layer.props?.crop || { x: 0, y: 0, w: 1, h: 1 };
	return {
		naturalW, naturalH,
		left: c.x * naturalW, top: c.y * naturalH,
		right: (c.x + c.w) * naturalW, bottom: (c.y + c.h) * naturalH,
		x: layer.x, y: layer.y,
	};
}

// Applies one crop-handle drag to a `startCropResize` snapshot. handle is a resize direction ('n', 'nw', 'w', ...).
// dx/dy are the raw drag distance in the layer's own unrotated frame, in canvas px. Returns the box's new x/y/w/h
// and the new crop fraction together; the box moves/shrinks exactly as far as the drag, so nothing rescales, and
// dragging back out (towards 0) uncrops, clamped at the layer's own full extent.
export function applyCropResize(start, handle) {
	return (dx, dy) => {
		let { left, top, right, bottom } = start;
		if(handle.includes('w')) left = clamp(left + dx, 0, right - MIN_BOX);
		if(handle.includes('e')) right = clamp(right + dx, left + MIN_BOX, start.naturalW);
		if(handle.includes('n')) top = clamp(top + dy, 0, bottom - MIN_BOX);
		if(handle.includes('s')) bottom = clamp(bottom + dy, top + MIN_BOX, start.naturalH);
		const x = start.x + (left - start.left);
		const y = start.y + (top - start.top);
		const w = right - left, h = bottom - top;
		return {
			x: round(x), y: round(y), w: Math.max(1, Math.round(w)), h: Math.max(1, Math.round(h)),
			crop: { x: round(left / start.naturalW), y: round(top / start.naturalH), w: round(w / start.naturalW), h: round(h / start.naturalH) },
		};
	};
}

// A crop fraction close enough to "the whole thing" that it isn't a crop at all any more.
export function isFullCrop(crop) {
	return !crop || (crop.x <= 0.0005 && crop.y <= 0.0005 && crop.w >= 0.9995 && crop.h >= 0.9995);
}

// The crop as 4 trimmed-off amounts, in canvas px — same as OBS's own Left/Top/Right/Bottom crop fields.
export function cropInsets(layer) {
	const { w: naturalW, h: naturalH } = naturalSize(layer);
	const c = layer.props?.crop;
	if(!c) return { left: 0, top: 0, right: 0, bottom: 0 };
	return {
		left: c.x * naturalW,
		top: c.y * naturalH,
		right: (1 - c.x - c.w) * naturalW,
		bottom: (1 - c.y - c.h) * naturalH,
	};
}

// Sets one crop inset (canvas px, >= 0) to an exact value typed into a field — the same effect dragging that
// edge's handle to match would have; the opposite edge doesn't move.
export function setCropInset(layer, side, value) {
	const start = startCropResize(layer);
	const delta = Math.max(0, value) - cropInsets(layer)[side];
	const handle = { left: 'w', right: 'e', top: 'n', bottom: 's' }[side];
	// A bigger right/bottom inset moves that edge inward (smaller natural-space position), the opposite of left/top.
	const signed = (side === 'right' || side === 'bottom') ? -delta : delta;
	return applyCropResize(start, handle)(handle === 'w' || handle === 'e' ? signed : 0, handle === 'n' || handle === 's' ? signed : 0);
}
